/** Читаемая строка из конверта ошибки API.
 *
 *  Отдельный модуль, потому что 422 у нас несёт разбор по полям в
 *  `details.errors`, и до сих пор его не читал НИКТО: каждый обработчик
 *  показывал `error.message`, то есть «Request payload failed validation.»
 *  — предложение, из которого не следует ничего. Пресет Early Stage Guard
 *  четыре дня отвергался за одно лишнее поле, и эта строка была всем, что
 *  видел пользователь.
 */

type ApiErrorItem = {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
};

type ApiEnvelope = {
  response?: {
    data?: {
      error?: {
        code?: string;
        message?: string;
        details?: { errors?: ApiErrorItem[] };
      };
    };
  };
};

/** Союз схем (`preset_config`) даёт по ошибке на КАЖДЫЙ вариант — десятки
 *  строк, из которых верна одна ветка. Pydantic кладёт имя варианта вторым
 *  элементом `loc`; ближайший к присланному — тот, у кого претензий меньше
 *  всего. Его и показываем, остальные варианты к делу не относятся. */
function narrowUnion(errors: ApiErrorItem[]): ApiErrorItem[] {
  const byVariant = new Map<string, ApiErrorItem[]>();
  for (const e of errors) {
    const second = e.loc?.[1];
    // Имя pydantic-модели: слово с заглавной. Индекс массива или обычное
    // поле под это не подходят, и такие ошибки группировать нечем.
    const key =
      typeof second === "string" && /^[A-Z]/.test(second) ? second : "";
    if (!key) return errors;
    byVariant.set(key, [...(byVariant.get(key) ?? []), e]);
  }
  if (byVariant.size < 2) return errors;
  return [...byVariant.values()].sort((a, b) => a.length - b.length)[0];
}

function fieldName(e: ApiErrorItem): string {
  const loc = e.loc ?? [];
  const last = loc[loc.length - 1];
  return typeof last === "string" ? last : loc.join(".");
}

/** Описание ошибки для показа пользователю. `fallback` — что сказать,
 *  когда сервер не дал ни кода, ни разбора. */
export function describeApiError(e: unknown, fallback: string): string {
  const err = e as ApiEnvelope;
  const envelope = err.response?.data?.error;
  if (!envelope) return fallback;

  const errors = envelope.details?.errors;
  if (errors && errors.length) {
    const lines = narrowUnion(errors)
      .slice(0, 3)
      .map((x) => `${fieldName(x)}: ${x.msg ?? "invalid"}`);
    if (lines.length) return lines.join("; ");
  }
  return envelope.message ?? envelope.code ?? fallback;
}
